import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { withAppHOC } from "../../hoc";
import API from "../../api";
import { API_RESPONSE_TYPES } from "../../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBank,
  faClose,
  faExclamation,
  faIndianRupeeSign,
  faListDots,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import { formatCurrency } from "../../utils";
import { Grid, GridItem } from "../../components/Grid";
import { TableBuilder } from "../../components/TableElements";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

const StyledHeading = styled.h1`
  font-size: 24px;
  font-weight: normal;
  color: rgb(0, 0, 0);
  margin: 20px 0px;
  padding: 0px;
`;

const StyledCountersContainer = styled.section`
  width: 100%;
  height: auto;
  margin: 0px;
  padding: 30px 0px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledCounter = styled.section`
  width: 100%;
  min-width: 300px;
  height: auto;
  border: 1px solid #e2e8ee;
  display: flex;
  flex-direction: row;
  border-radius: 7px;
  border-top-width: 3px;
  border-top-color: ${({ borderTopColor }) => borderTopColor};
`;

const StyledCounterIcon = styled.section`
  width: auto;
  padding: 20px;
  svg {
    margin: 0px;
    background-color: ${({ iconBgColor }) => iconBgColor};
    width: 25px;
    height: 25px;
    padding: 10px;
    color: #fff;
    border-radius: 3px;
  }
`;

const StyledCounterData = styled.section`
  flex: 1;
  padding: 20px 0px;
  p {
    font-size: 14px;
    font-weight: normal;
    color: #000000;
    margin: 0px;
    padding: 0px;
  }
  h2 {
    font-family: "Space Grotesk", sans-serif;
    font-weight: 400 !important;
    font-size: 24px;
    color: #000000;
    margin: 15px 0px;
    padding: 0px;
  }
  span {
    color: ${({ spanTextColour }) => spanTextColour};
    background-color: ${({ spanBgColour }) => spanBgColour};
    padding: 5px 15px;
    margin: 0px;
    border-radius: 5px;
    font-size: 10px;
    display: inline-block;
  }
`;

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState();

  const readStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await API.get("/dashboard/stats-new");
      const { status, data } = response.data;
      if (status === API_RESPONSE_TYPES.SUCCESS) {
        setStats(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    readStats();
  }, [readStats]);

  return (
    <>
      <StyledCountersContainer>
        {isLoading ? (
          <LoadingSpinner isLoading={isLoading} />
        ) : (
          <>
            <Grid>
              <GridItem>
                <StyledCounter borderTopColor={"rgb(0 128 1)"}>
                  <StyledCounterIcon iconBgColor={"rgb(0 128 1)"}>
                    <FontAwesomeIcon icon={faIndianRupeeSign} />
                  </StyledCounterIcon>
                  <StyledCounterData
                    spanBgColour={"rgb(209, 243, 209)"}
                    spanTextColour={"rgb(0 128 1)"}
                  >
                    <p>Pending Transaction</p>
                    <h2>{stats?.pendingTransaction}</h2>
                    <span>Today</span>
                  </StyledCounterData>
                </StyledCounter>
              </GridItem>
              <GridItem>
                <StyledCounter borderTopColor={"#dc3545"}>
                  <StyledCounterIcon iconBgColor={"#dc3545"}>
                    <FontAwesomeIcon icon={faExclamation} />
                  </StyledCounterIcon>
                  <StyledCounterData
                    spanBgColour={"#ffe9eb"}
                    spanTextColour={"#dc3545"}
                  >
                    <p>Pending Complaints</p>
                    <h2>{stats?.pendingComplaints}</h2>
                    <span>Today</span>
                  </StyledCounterData>
                </StyledCounter>
              </GridItem>
              <GridItem>
                <StyledCounter borderTopColor={"#0078ce"}>
                  <StyledCounterIcon iconBgColor={"#0078ce"}>
                    <FontAwesomeIcon icon={faClose} />
                  </StyledCounterIcon>
                  <StyledCounterData
                    spanBgColour={"#cceaff"}
                    spanTextColour={"#0078ce"}
                  >
                    <p>Fake Complaints Resolved</p>
                    <h2>{stats?.fakeComplaintsResolved}</h2>
                    <span>Today</span>
                  </StyledCounterData>
                </StyledCounter>
              </GridItem>
              <GridItem>
                <StyledCounter borderTopColor={"#04c9ff"}>
                  <StyledCounterIcon iconBgColor={"#04c9ff"}>
                    <FontAwesomeIcon icon={faListDots} />
                  </StyledCounterIcon>
                  <StyledCounterData
                    spanBgColour={"#d8f7ff"}
                    spanTextColour={"#04c9ff"}
                  >
                    <p>Total Transaction</p>
                    <h2>{formatCurrency(stats?.todaysTransactionsTotal)}</h2>
                    <span>Today</span>
                  </StyledCounterData>
                </StyledCounter>
              </GridItem>
            </Grid>
            <Grid>
              <GridItem>
                <StyledCounter borderTopColor={"#33d484"}>
                  <StyledCounterIcon iconBgColor={"#33d484"}>
                    <FontAwesomeIcon icon={faWallet} />
                  </StyledCounterIcon>
                  <StyledCounterData
                    spanBgColour={"#e4fff2"}
                    spanTextColour={"#33d484"}
                  >
                    <p>Wallet Topup</p>
                    <h2>{formatCurrency(stats?.todayWalletTopup)}</h2>
                    <span>Today</span>
                  </StyledCounterData>
                </StyledCounter>
              </GridItem>
              <GridItem>
                <StyledCounter borderTopColor={"#985ffd"}>
                  <StyledCounterIcon iconBgColor={"#985ffd"}>
                    <FontAwesomeIcon icon={faWallet} />
                  </StyledCounterIcon>
                  <StyledCounterData
                    spanBgColour={"#f4edff"}
                    spanTextColour={"#985ffd"}
                  >
                    <p>Buyers Wallet</p>
                    <h2>{formatCurrency(stats?.userBuyerWallet)}</h2>
                    <span>Today</span>
                  </StyledCounterData>
                </StyledCounter>
              </GridItem>
              <GridItem>
                <StyledCounter borderTopColor={"#ff49cd"}>
                  <StyledCounterIcon iconBgColor={"#ff49cd"}>
                    <FontAwesomeIcon icon={faWallet} />
                  </StyledCounterIcon>
                  <StyledCounterData
                    spanBgColour={"#ffe4f8"}
                    spanTextColour={"#ff49cd"}
                  >
                    <p>Sellers Wallet</p>
                    <h2>{formatCurrency(stats?.userSellerWallet)}</h2>
                    <span>Today</span>
                  </StyledCounterData>
                </StyledCounter>
              </GridItem>
              <GridItem>
                <StyledCounter borderTopColor={"#feaf23"}>
                  <StyledCounterIcon iconBgColor={"#feaf23"}>
                    <FontAwesomeIcon icon={faBank} />
                  </StyledCounterIcon>
                  <StyledCounterData
                    spanBgColour={"#ffeed0"}
                    spanTextColour={"#feaf23"}
                  >
                    <p>Wallet Redeem</p>
                    <h2>{formatCurrency(stats?.todayWalletRedeem)}</h2>
                    <span>Today</span>
                  </StyledCounterData>
                </StyledCounter>
              </GridItem>
            </Grid>
            <Grid>
              <GridItem>
                <StyledHeading>Sales Summary</StyledHeading>
                <TableBuilder
                  tableHeadings={[
                    {
                      title: "Date",
                      dataSelector: "date",
                      dataType: "string",
                    },
                    {
                      title: "Amount",
                      dataSelector: "amount",
                      dataType: "string",
                      CellRenderer: (value) => formatCurrency(value),
                    },
                    {
                      title: "Profit",
                      dataSelector: "profit",
                      dataType: "string",
                      CellRenderer: (value) => formatCurrency(value),
                    },
                  ]}
                  tableData={stats?.transactionsSummary}
                />
              </GridItem>
              <GridItem>
                <StyledHeading>Recent User Registrations</StyledHeading>
                <TableBuilder
                  tableHeadings={[
                    {
                      title: "User Details",
                      dataSelector: "fullName",
                      dataType: "string",
                    },
                    {
                      title: "Company Name",
                      dataSelector: "firmName",
                      dataType: "string",
                      CellRenderer: (value) => (value ? value : "N/A"),
                    },
                    {
                      title: "Mobile",
                      dataSelector: "mobileNumber",
                      dataType: "string",
                    },
                    {
                      title: "Date",
                      dataSelector: "createdOn",
                      dataType: "string",
                    },
                  ]}
                  tableData={stats?.recentAddedUsers}
                />
              </GridItem>
            </Grid>
            <Grid>
              <GridItem>
                <StyledHeading>Todays Seller's Sales</StyledHeading>
                <TableBuilder
                  tableHeadings={[
                    {
                      title: "Company Name",
                      dataSelector: "companyName",
                      dataType: "string",
                    },
                    {
                      title: "Amount",
                      dataSelector: "amount",
                      dataType: "string",
                      CellRenderer: (value) => formatCurrency(value),
                    },
                  ]}
                  tableData={stats?.sellerSales}
                />
              </GridItem>
              <GridItem>
                <StyledHeading>Todays Buyer's Sales</StyledHeading>
                <TableBuilder
                  tableHeadings={[
                    {
                      title: "Company Name",
                      dataSelector: "companyName",
                      dataType: "string",
                    },
                    {
                      title: "Amount",
                      dataSelector: "amount",
                      dataType: "string",
                      CellRenderer: (value) => formatCurrency(value),
                    },
                  ]}
                  tableData={stats?.buyerSales}
                />
              </GridItem>
            </Grid>
          </>
        )}
      </StyledCountersContainer>
    </>
  );
};

export default withAppHOC(Dashboard);
